/**
 * Zone tint — a face set, seen on the character (ADR-031 T1).
 *
 * The affirmation strip's first question is "is any of the tail outside the
 * zone?" and that is answered by looking: the zone's faces wear a colour, the
 * rest of the flesh wears the texture. This layer paints Document zones onto
 * the subject and decides nothing about them.
 *
 * WHAT IT DRAWS. A GHOST SkinnedMesh over the subject's sole skinned mesh, on
 * the same skeleton and morph state (the ADR-027 ghost pattern, stamped
 * through the ONE debris identity so no door ships or counts it). Unlike the
 * weight overlay's ghost this one OWNS A NON-INDEXED COPY of the geometry:
 * a zone is a set of FACES, and a per-vertex colour on shared vertices would
 * bleed one triangle across every zone boundary. `toNonIndexed()` gives each
 * face its own three vertices — position, normal, uv, skin and morph
 * attributes copied (a few MB on a 12K-vertex character) — so the tint is
 * crisp to the triangle, which is what a person judging a boundary needs.
 *
 * DEPTH-TESTED, OFFSET TOWARD THE EYE. Faces are triangles, so the classic
 * polygon offset works here (unlike the wire's lines): the tint sits on the
 * flesh without z-fighting it and hidden faces stay hidden, so a tail tint
 * reads as a tail, not an x-ray.
 *
 * FAIL-LOUD (§7): `show` returns null on success or a refusal string; a face
 * index past the mesh's face count refuses by name — a zone cut on another
 * mesh must never light the wrong triangles (D2 lives one door up, in the
 * fingerprint check; this is the belt to its braces).
 *
 * Depends on: three, adapter-three (identity, sole mesh). Depended on by:
 * main (the zone proposer and the flyout's anchor).
 */
import { BufferAttribute, BufferGeometry, Float32BufferAttribute, MeshBasicMaterial, Object3D, SkinnedMesh, Vector3 } from 'three';
import { isViewportDebris, markViewportDebris } from '@harryhausen/adapter-three';

export interface TintedZone {
  readonly id: string;
  readonly faces: ArrayLike<number>;
  /** Linear RGB, 0..1. */
  readonly color: readonly [number, number, number];
}

export interface ZoneTint {
  /** Paint these zones (and nothing else). Null on success, else a refusal to surface. */
  show(zones: readonly TintedZone[]): string | null;
  /** The POSED world position of a zone's representative face — where a flyout attaches. Null if not shown. */
  anchorWorld(id: string): { x: number; y: number; z: number } | null;
  setOpacity(opacity: number): void;
  hide(): void;
  dispose(): void;
  probe(): { on: boolean; zones: number; paintedFaces: number; opacity: number };
}

const DEFAULT_OPACITY = 0.55;

function soleSource(root: Object3D): SkinnedMesh | null {
  let found: SkinnedMesh | null = null;
  let count = 0;
  root.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh && !isViewportDebris(o)) {
      count++;
      if (found === null) found = o as SkinnedMesh;
    }
  });
  return count === 1 ? found : null;
}

export function createZoneTint(getScene: () => Object3D | null): ZoneTint {
  let source: SkinnedMesh | null = null;
  let ghost: SkinnedMesh | null = null;
  let material: MeshBasicMaterial | null = null;
  let faceCount = 0;
  let on = false;
  let opacity = DEFAULT_OPACITY;
  let painted = 0;
  /** zone id → a representative face index (the first), for the anchor. */
  const anchors = new Map<string, number>();

  function teardown(): void {
    if (ghost !== null) {
      ghost.removeFromParent();
      ghost.geometry.dispose(); // OURS: the non-indexed copy
      material?.dispose();
    }
    ghost = null;
    material = null;
    source = null;
    faceCount = 0;
    painted = 0;
    anchors.clear();
  }

  function ensureGhost(): string | null {
    const scene = getScene();
    if (!scene) {
      teardown();
      return 'no subject loaded';
    }
    const mesh = soleSource(scene);
    if (mesh === null) {
      teardown();
      return 'subject has no sole skinned mesh';
    }
    if (mesh === source && ghost !== null) return null;
    teardown();
    const src = mesh.geometry;
    // Every face gets its own corners; the source's own `color` (if any) is
    // dropped and replaced by our RGBA — the weight overlay's rule (#98).
    const geometry: BufferGeometry = src.index !== null ? src.toNonIndexed() : src.clone();
    geometry.deleteAttribute('color');
    const count = geometry.getAttribute('position').count;
    geometry.setAttribute('color', new Float32BufferAttribute(new Float32Array(count * 4), 4));
    const mat = new MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const g = new SkinnedMesh(geometry, mat);
    markViewportDebris(g, 'zone-tint'); // the ONE identity every door reads
    g.renderOrder = (mesh.renderOrder ?? 0) + 1;
    g.skeleton = mesh.skeleton;
    g.bindMode = mesh.bindMode;
    g.bind(mesh.skeleton, mesh.bindMatrix);
    g.frustumCulled = false;
    if (mesh.morphTargetInfluences) g.morphTargetInfluences = mesh.morphTargetInfluences;
    if (mesh.morphTargetDictionary) g.morphTargetDictionary = mesh.morphTargetDictionary;
    mesh.add(g); // a CHILD at identity: inherits the live transform and visibility
    source = mesh;
    ghost = g;
    material = mat;
    faceCount = count / 3;
    return null;
  }

  return {
    show(zones: readonly TintedZone[]): string | null {
      const refusal = ensureGhost();
      if (refusal !== null) {
        this.hide();
        return refusal;
      }
      const color = ghost!.geometry.getAttribute('color') as BufferAttribute;
      const data = color.array as Float32Array;
      data.fill(0);
      anchors.clear();
      painted = 0;
      for (const zone of zones) {
        const [r, g, b] = zone.color;
        for (let k = 0; k < zone.faces.length; k++) {
          const f = zone.faces[k]!;
          if (f < 0 || f >= faceCount || !Number.isInteger(f)) {
            this.hide();
            return `zone "${zone.id}" names face ${f}, but this mesh has ${faceCount} faces — cut on another mesh?`;
          }
          for (let c = 0; c < 3; c++) {
            const v = (f * 3 + c) * 4;
            data[v] = r;
            data[v + 1] = g;
            data[v + 2] = b;
            data[v + 3] = 1;
          }
          painted++;
        }
        if (zone.faces.length > 0 && !anchors.has(zone.id)) anchors.set(zone.id, zone.faces[Math.floor(zone.faces.length / 2)]!);
      }
      color.needsUpdate = true;
      on = true;
      ghost!.visible = true;
      return null;
    },
    anchorWorld(id: string): { x: number; y: number; z: number } | null {
      if (!on || ghost === null) return null;
      const face = anchors.get(id);
      if (face === undefined) return null;
      // Skin-aware: the ghost rides the clip, so this is where the flesh IS.
      const p = ghost.getVertexPosition(face * 3, new Vector3()).applyMatrix4(ghost.matrixWorld);
      return { x: p.x, y: p.y, z: p.z };
    },
    setOpacity(value: number): void {
      opacity = Math.min(1, Math.max(0.1, value));
      if (material) material.opacity = opacity;
    },
    hide(): void {
      on = false;
      if (ghost) ghost.visible = false;
    },
    dispose(): void {
      teardown();
      on = false;
    },
    probe() {
      return { on, zones: anchors.size, paintedFaces: painted, opacity };
    },
  };
}
